import React from "react";
import { useCombobox } from "downshift";
import { useMenuChildren } from "../hooks/useMenuChildren";
import { itemToString as defaultItemToString } from "../utils/downshiftDefaults";
import { reduceReducers } from "../utils/reduceReducers";
import { useDownshiftDefaults } from "../hooks/useDownshiftDefaults";
import { useItemFiltering } from "../hooks/useItemFiltering";
import { MenuRoot } from "../MenuRoot";

import type { TypeMenuItemProps } from "../MenuItem";
import type { TypeSingleAutocompleteProps } from "./AutocompleteTypes";

export const SingleAutocomplete = <
  I extends TypeMenuItemProps = TypeMenuItemProps
>({
  children: childrenProp,
  stateReducer: externalStateReducer,
  menuItems,
  MenuRootComponent = MenuRoot,
  MenuItemComponent,
  itemToString = defaultItemToString,
  getIsItemVisible,
  autoCloseOnEmpty = true,
  ...useComboboxProps
}: TypeSingleAutocompleteProps<I>) => {
  /** Get a list of menuItems and children */
  const { allMenuItems, children } = useMenuChildren({
    children: childrenProp,
    menuItems,
    MenuItemComponent,
  });

  const downshiftDefaults = useDownshiftDefaults({ isCombobox: true });

  const { setHiddenItemsMap, defaultDownshiftProps, hiddenItemsCount } =
    downshiftDefaults;

  const { updateFilteredItems } = useItemFiltering({
    allMenuItems,
    itemToString,
    getIsItemVisible,
    setHiddenItemsMap,
  });

  /**
   * Destructure Downshift props and call the core useCombobox with items and state handler
   */
  const useComboboxReturnProps = useCombobox({
    ...defaultDownshiftProps.combobox,
    items: allMenuItems,
    itemToString,
    onInputValueChange: updateFilteredItems,
    stateReducer: reduceReducers(
      defaultDownshiftProps.combobox.stateReducer,
      externalStateReducer
    ),
    ...useComboboxProps,
  });

  const { isOpen, closeMenu, inputValue } = useComboboxReturnProps;
  const { popoutProps: userPopoutProps } = useComboboxProps;

  return (
    <MenuRootComponent
      {...{
        isListbox: true,
        autoCloseOnEmpty,
        items: allMenuItems,
        itemToString,
        ...useComboboxProps,
        ...useComboboxReturnProps,
        ...downshiftDefaults,
      }}
      popoutProps={{
        focusOnContent: false,
        ...userPopoutProps,
      }}
    >
      {children}
    </MenuRootComponent>
  );
};
